import { useCallback, useEffect, useMemo, useState } from 'react';
import type { WorkflowConfig } from '@reef/schema';
import { api, ConnectionsRequiredError, type RunSummary, type WorkflowSummary } from '../api.ts';
import { WorkflowGraph, countSteps } from '../components/WorkflowGraph.tsx';

/**
 * One factory workflow: what it takes in, what it has done, and what it does.
 *
 * The three panes are the three questions somebody arrives with. The run input
 * is on the left because starting a run is the reason most visits happen; the
 * recent runs sit under it because "has this worked before" is answered by the
 * same glance; and the graph is on the right because it is the thing you read
 * rather than fill in.
 *
 * The input form is generated from the factory's own work-item vocabulary
 * rather than from a free-text box. A work item typed at the door is a work
 * item every downstream step can rely on the shape of — which is the whole
 * argument for typing the handoffs in the first place, and it would be a
 * strange argument to make and then start every run with a paragraph.
 *
 * It asks only for what a person knows and reef does not: what happened, and
 * where. Priority was on this form too and is not any more — the work item
 * still carries one, but triage sets it, from the full signal and the current
 * backlog rather than from a title. Ranking a thing against eighty others is
 * not what the person filing it is in a position to do, and the number they
 * pick under that pressure is one the agent then has to decide whether to
 * contradict.
 */

export interface WorkItemDraft {
  title: string;
  description: string;
  repo: string;
}

const EMPTY_DRAFT: WorkItemDraft = {
  title: '',
  description: '',
  repo: '',
};

/**
 * The repository offered first, when the caller can reach it at all.
 *
 * Not a default — the field still starts empty, because a run that quietly
 * picked a repository nobody chose is worse than one that asks. It is only an
 * ordering: this is the repository most runs name, and putting it under a
 * dozen alphabetised remotes makes the common case the slowest one.
 */
const PINNED_REPO = 'onboardiq/megalith';

/*
 * The form starts the workflow on screen, and for now that is all it does.
 *
 * It should file described work at the factory's front door — `factory-sdlc`
 * reads what arrived, ranks it, and starts the stage it belongs in, which is
 * the path every other entry point already takes and the one a hand-started
 * run skips. That routing was written and then taken back out, because
 * `factory-sdlc` cannot currently start: it mounts `reef` and
 * `factory-metrics`, both session-gated loopbacks that `preflightConnections`
 * refuses to a step, so every run through the front door would fail before its
 * first step.
 *
 * Making one workflow the destination for everything described here is only an
 * improvement if that workflow runs. It lands with the change that lets a step
 * mount reef's session-gated servers, and not before.
 */

/**
 * The pinned repository first, then the rest in the order they arrived.
 *
 * A reorder and never an insert. `GET /factory/repos` has already narrowed the
 * list to what this caller can reach, and a client that added a name back
 * would undo that narrowing where it is least visible — somebody with no path
 * to `onboardiq/megalith` would still read it off a dropdown, and learning a
 * repository exists is the disclosure the endpoint's filter is there to
 * prevent. Selecting it would fail at the clone, which is a worse way to find
 * out than not being offered it.
 */
export function orderRepos(repos: readonly string[]): string[] {
  const rest = repos.filter((repo) => repo !== PINNED_REPO);
  return repos.includes(PINNED_REPO) ? [PINNED_REPO, ...rest] : rest;
}

/**
 * A work item id, minted here rather than typed.
 *
 * The id identifies the item to reef and to nothing else — no human reads it,
 * no external system reconciles against it. Asking somebody to invent one is
 * asking them to do a machine's job before they may describe their actual
 * problem, and an id invented under that pressure is `test` twice: two runs
 * filed against one item, which every downstream step keys off.
 */
export function newWorkItemId(): string {
  return `wi-${crypto.randomUUID().slice(0, 8)}`;
}

/**
 * One id per work item, not one per click.
 *
 * A start that failed keeps its id: the item is the same item, nothing
 * downstream saw it, and a retry that arrived under a new id would look like a
 * second piece of work rather than a second attempt at the first. A start that
 * succeeded rotates: the next thing typed into this form is a different item,
 * and reusing the id would file its run against the one already in flight —
 * every step keys off that id, so the two would silently merge.
 *
 * The rule lives here rather than at the two call sites it would otherwise be
 * split across, because "rotate on success" expressed as a line's position
 * inside a `try` is a rule nothing can assert against.
 */
export function nextWorkItemId(current: string, outcome: 'started' | 'failed'): string {
  return outcome === 'started' ? newWorkItemId() : current;
}

/**
 * The same rotation, applied to a payload somebody has been editing by hand.
 *
 * The JSON view is not a preview — it is what gets sent, and the id in it is
 * the one the run is filed under. Rotating only the form's copy left the
 * textarea holding the id that had just been used, so a second Run in JSON
 * mode submitted a duplicate: two distinct pieces of work under one id, which
 * every downstream step keys off and would therefore merge. Nothing would
 * report an error; the second run would simply attach its pull request to the
 * first item.
 *
 * Only the id moves. Every other edit is deliberate — that is the entire
 * reason the JSON view exists — and is preserved verbatim, including field
 * order. `signal.externalId` follows only when it still matches the id being
 * replaced, because a person who put a real issue number there meant it.
 *
 * Unparseable text is returned untouched. It cannot have started a run, so
 * there is nothing to rotate away from, and rewriting somebody's half-finished
 * edit into `{}` would be a worse answer than leaving it alone.
 */
export function rotateIdIn(json: string, from: string, to: string): string {
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(json) as Record<string, unknown>;
  } catch {
    return json;
  }

  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return json;
  if (parsed.id !== from) return json;

  const signal = parsed.signal;
  const next: Record<string, unknown> = { ...parsed, id: to };

  if (signal !== null && typeof signal === 'object' && !Array.isArray(signal)) {
    const carried = signal as Record<string, unknown>;
    if (carried.externalId === from) next.signal = { ...carried, externalId: to };
  }

  return JSON.stringify(next, null, 2);
}

/** What actually goes to the run: the draft, as the work item the steps expect. */
export function draftToWorkItem(draft: WorkItemDraft, id: string): Record<string, unknown> {
  return {
    id,
    title: draft.title.trim(),
    description: draft.description.trim(),
    repo: draft.repo || null,
    signal: {
      source: 'manual',
      externalId: id,
      title: draft.title.trim(),
      observedAt: new Date().toISOString(),
    },
  };
}

export function FactoryWorkflowsView({
  workflows,
  factoryIds,
  workflowId,
  onSelect,
}: {
  workflows: WorkflowSummary[];
  /** Ids the control plane says are part of the factory, in loop order. */
  factoryIds: string[];
  /**
   * From the path — `/workflows/:id/run`, which is where a catalogue row's Run
   * lands. Null when the form was reached from the strip, and then the first
   * factory workflow stands in.
   */
  workflowId: string | null;
  /** The picker's choice, reported back so the address bar keeps up with it. */
  onSelect(id: string): void;
}) {
  /*
   * The factory's own workflows first and in the order work moves through them,
   * then everything else. A studio that sorted these alphabetically would put
   * validation before triage, which is the one ordering that misleads.
   */
  const ordered = useMemo(() => {
    const byId = new Map(workflows.map((workflow) => [workflow.id, workflow]));
    const factory = factoryIds.flatMap((id) => (byId.has(id) ? [byId.get(id)!] : []));
    const rest = workflows.filter((workflow) => !factoryIds.includes(workflow.id));
    return { factory, rest };
  }, [workflows, factoryIds]);

  /*
   * Which workflow is selected lives in the URL rather than here, so a
   * catalogue row's Run and a pasted link both land on it picked. The fallback
   * is derived during render rather than written from an effect — the effect
   * that used to set it was scheduled against the render before a reader's
   * click, so it could land after their choice and undo it.
   */
  const selectedId = workflowId ?? ordered.factory[0]?.id ?? workflows[0]?.id ?? null;
  const [workflow, setWorkflow] = useState<WorkflowConfig | null>(null);
  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [mode, setMode] = useState<'form' | 'json'>('form');
  const [draft, setDraft] = useState<WorkItemDraft>(EMPTY_DRAFT);
  const [workItemId, setWorkItemId] = useState(newWorkItemId);
  const [repos, setRepos] = useState<string[]>([]);
  const [json, setJson] = useState('{\n  \n}');
  const [starting, setStarting] = useState(false);
  const [started, setStarted] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /*
   * Once per visit rather than per workflow: the reach is the person's, not the
   * workflow's, so refetching it on every selection would be the same answer
   * bought again.
   */
  useEffect(() => {
    let current = true;
    api
      .factoryRepos()
      .then((result) => current && setRepos(result.repos))
      .catch(() => current && setRepos([]));
    return () => {
      current = false;
    };
  }, []);

  const repoOptions = useMemo(() => orderRepos(repos), [repos]);

  const refreshRuns = useCallback((id: string) => {
    api
      .listRuns(id)
      .then((result) => setRuns(result.runs))
      .catch(() => setRuns([]));
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    let current = true;

    setWorkflow(null);
    setError(null);
    setStarted(null);

    api
      .getWorkflow(selectedId)
      .then((result) => {
        if (!current) return;
        setWorkflow(result.workflow);
      })
      .catch((caught) => current && setError((caught as Error).message));

    refreshRuns(selectedId);

    return () => {
      current = false;
    };
  }, [selectedId, refreshRuns]);

  // Kept in sync one way only: switching to JSON shows what the form built.
  // Switching back does not parse the JSON into the form, because a hand-edited
  // payload with fields the form has no input for would silently lose them.
  const switchMode = (next: 'form' | 'json') => {
    if (next === 'json') setJson(JSON.stringify(draftToWorkItem(draft, workItemId), null, 2));
    setMode(next);
  };

  const run = async () => {
    if (!selectedId) return;
    setStarting(true);
    setError(null);
    setStarted(null);

    let outcome: 'started' | 'failed' = 'failed';

    try {
      const payload =
        mode === 'form' ? draftToWorkItem(draft, workItemId) : (JSON.parse(json) as unknown);
      const result = await api.runWorkflow(
        selectedId,
        JSON.stringify(payload, null, 2),
        /*
         * The same object, sent as itself rather than only as prose. The
         * control plane parses it, fills in the facts that are its to state —
         * the timestamps, the unranked priority, the arrival state — and
         * stores it for the steps to read.
         *
         * Both modes, and that is the point. Sending it only from the form
         * meant a run started from the JSON view had no work item row, so its
         * first step's `item.get()` found nothing — and a hand-started
         * `factory-codegen`, which is exactly what the JSON view is used for,
         * is the case that needs the row most. The payload is the work item in
         * both modes; the only difference is who typed it.
         *
         * A hand-edit that is no longer a work item now comes back as a 400
         * naming the field, before the run starts. That is a narrowing of what
         * the JSON view accepts, and the right one: the alternative is a run
         * that begins and then cannot read its own subject.
         */
        payload,
      );
      outcome = 'started';
      setStarted(result.runId);
      refreshRuns(selectedId);
    } catch (caught) {
      setError(
        caught instanceof ConnectionsRequiredError
          ? `${caught.message} ${caught.missing.map((entry) => entry.connectionName).join(', ')}`
          : (caught as Error).message,
      );
    } finally {
      // Both outcomes go through the same call, so the id's lifetime is one
      // rule in one place rather than a line's position inside the `try`.
      setWorkItemId((current) => {
        const next = nextWorkItemId(current, outcome);
        // The JSON view is what gets sent, so it has to move with the form's
        // copy. Left behind, it would resubmit the id the last run used.
        if (next !== current) setJson((text) => rotateIdIn(text, current, next));
        return next;
      });
      setStarting(false);
    }
  };

  const valid = mode === 'json' || draft.title.trim().length > 0;

  /*
   * Nothing to select, so nothing to load — said plainly, before the picker and
   * the canvas.
   *
   * `selectedId` is null when the caller can see no workflow, and the effect
   * that fetches one returns immediately on that. So `workflow` stays null and
   * `error` stays null, and the canvas rendered "Loading the workflow…" for as
   * long as anybody was willing to watch it: the one state where the screen has
   * nothing to do is the one where it claimed to be busy. It is reachable two
   * ways — a deployment with an empty catalog, which is this one, and a member
   * shared on no workflow at all, which is a permissions answer rather than a
   * configuration one, so the copy names both.
   *
   * After the hooks, deliberately: an early return above them changes how many
   * run on this render.
   */
  if (workflows.length === 0) {
    return (
      <div className="studio">
        <header className="studio-head">
          <span className="studio-crumb">
            <span className="studio-crumb-root">Workflows</span>
          </span>
        </header>
        <div className="empty-state">
          <h3>No workflow to run</h3>
          <p>
            Nothing is shared with you, or this deployment has no workflows yet. A workflow is
            created in the builder, or through <code>create_workflow</code> on reef's own MCP
            server.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="studio">
      <header className="studio-head">
        <span className="studio-crumb">
          <span className="studio-crumb-root">Workflows</span>
          <span className="studio-crumb-sep">/</span>
          <select
            className="studio-crumb-select"
            aria-label="Workflow"
            value={selectedId ?? ''}
            onChange={(event) => onSelect(event.target.value)}
          >
            {/*
              * Grouped only when there is something to group. With no factory
              * workflows deployed the picker was an empty "Reef" group above an
              * "Other workflows" one holding all of them — two labels that
              * between them said the deployment had nothing and everything.
              */}
            {ordered.factory.length > 0 && (
              <optgroup label="Reef">
                {ordered.factory.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.id}
                  </option>
                ))}
              </optgroup>
            )}
            {ordered.rest.length > 0 &&
              (ordered.factory.length > 0 ? (
                <optgroup label="Other workflows">
                  {ordered.rest.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.id}
                    </option>
                  ))}
                </optgroup>
              ) : (
                ordered.rest.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.id}
                  </option>
                ))
              ))}
          </select>
        </span>

        <span className="spacer" />

        <a
          className="studio-head-link"
          href="https://github.com/onboardiq/reef/blob/main/docs/factory/README.md"
          target="_blank"
          rel="noreferrer"
        >
          Factory documentation
        </a>
      </header>

      <div className="studio-split">
        <div className="studio-column">
          <section className="studio-card">
            <div className="studio-card-head">
              <h2>{workflow?.id ?? selectedId ?? 'No workflow'}</h2>
              {workflow && <span className="chip">{countSteps(workflow.steps)} steps</span>}
            </div>

            <div className="studio-card-body">
              <div className="studio-row">
                <p className="section-title" style={{ margin: 0 }}>
                  Run input
                </p>
                <div className="toggle" role="group" aria-label="Input format">
                  <button
                    type="button"
                    aria-pressed={mode === 'form'}
                    onClick={() => switchMode('form')}
                  >
                    Form
                  </button>
                  <button
                    type="button"
                    aria-pressed={mode === 'json'}
                    onClick={() => switchMode('json')}
                  >
                    {'{ } JSON'}
                  </button>
                </div>
              </div>

              {mode === 'form' ? (
                <div className="studio-form">
                  <label>
                    <span>
                      Title <span className="required">*</span>
                    </span>
                    <input
                      value={draft.title}
                      onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                    />
                  </label>

                  <label>
                    Description
                    <input
                      value={draft.description}
                      onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                    />
                  </label>

                  <label>
                    Repo
                    <select
                      value={draft.repo}
                      onChange={(event) => setDraft({ ...draft, repo: event.target.value })}
                    >
                      <option value="">No repo</option>
                      {repoOptions.map((repo) => (
                        <option key={repo} value={repo}>
                          {repo}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              ) : (
                <textarea
                  className="studio-json"
                  rows={14}
                  value={json}
                  onChange={(event) => setJson(event.target.value)}
                  spellCheck={false}
                />
              )}

              <div className="studio-row">
                {/*
                 * Named rather than "Run workflow", which is the one thing the
                 * routing experiment left behind that was worth keeping: the
                 * button says which workflow a click starts, and that stays
                 * true when the front door eventually sends it somewhere else.
                 */}
                <button className="primary" disabled={!valid || starting || !workflow} onClick={run}>
                  {starting ? 'Starting…' : `Run ${selectedId ?? 'workflow'}`}
                </button>
                {started && <span className="hint">Started {started}</span>}
              </div>

              {error && <div className="banner danger">{error}</div>}
            </div>
          </section>

          <section className="studio-card">
            <div className="studio-card-head">
              <h2>Recent runs</h2>
            </div>
            <div className="studio-card-body">
              {runs.length === 0 ? (
                <p className="hint">Your run history will appear here once you run the workflow.</p>
              ) : (
                <ul className="studio-runs">
                  {runs.slice(0, 12).map((entry) => (
                    <li key={entry.id}>
                      <span className={`run-dot ${entry.status}`} aria-hidden="true" />
                      <span className="studio-run-id">{entry.id}</span>
                      <span className="hint">{entry.triggerType}</span>
                      <span className="hint studio-run-status">{entry.status}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </div>

        <section className="studio-canvas">
          {workflow ? (
            <WorkflowGraph workflow={workflow} />
          ) : (
            <div className="empty">
              {error ? 'That workflow could not be loaded.' : 'Loading the workflow…'}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
